'use client';

import * as React from 'react';
import { cn } from '../utils';
import { useTeaseAnalytics } from './TeaseAnalytics';

export interface TeaseUpsellProps {
  /** Top headline, e.g. "Unlock EV". */
  title: string;
  /** One-line value prop, e.g. "Per-line edge over the market, refreshed every minute." */
  body: string;
  /** CTA button label. Omit to render a non-actionable body (e.g. "coming soon"). */
  ctaLabel?: string;
  /** CTA target. Plain anchor so this package doesn't depend on next/link. */
  ctaHref?: string;
  /** Optional secondary link (e.g. "Already have an account? Log in"). */
  secondaryLabel?: string;
  secondaryHref?: string;
  className?: string;
  /** Analytics label for where this prompt appeared (e.g. "table_ev"). Reported via
   *  TeaseAnalyticsProvider; defaults to the title. */
  surface?: string;
}

/**
 * Shared upsell body for the "hidden EV / hidden projection" tease surfaces.
 * Rendered inside a desktop popover or a mobile vaul drawer via RichPopover.
 * Kept presentational — link targets are passed in from the app layer.
 */
export const TeaseUpsell: React.FC<TeaseUpsellProps> = ({
  title,
  body,
  ctaLabel,
  ctaHref,
  secondaryLabel,
  secondaryHref,
  className,
  surface,
}) => {
  const analytics = useTeaseAnalytics();
  const label = surface ?? title;
  // Mounted only while its popover is open (Popover renders children on open), so
  // mount = the prompt was actually seen. Only actionable prompts count as upsells.
  const actionable = !!(ctaLabel && ctaHref);
  React.useEffect(() => {
    if (actionable) analytics?.onView(label);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per open
  }, []);

  return (
    <div className={cn('flex flex-col gap-3 px-4 py-3', className)}>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold tracking-tight text-fg dark:text-text-primary">{title}</p>
        <p className="text-xs text-muted-foreground dark:text-text-secondary leading-snug">{body}</p>
      </div>
      {ctaLabel && ctaHref && (
        <a
          href={ctaHref}
          onClick={() => analytics?.onClick(label, 'cta', ctaHref)}
          className={cn(
            'inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm',
            'bg-brand-500 hover:bg-brand-600 active:bg-brand-700',
            'dark:bg-brand-600 dark:hover:bg-brand-500 dark:active:bg-brand-700',
            'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:focus-visible:ring-brand-400 focus-visible:ring-offset-2',
          )}
        >
          {ctaLabel}
        </a>
      )}
      {secondaryLabel && secondaryHref && (
        <a
          href={secondaryHref}
          onClick={() => analytics?.onClick(label, 'secondary', secondaryHref)}
          className="text-center text-xs text-muted-foreground dark:text-text-secondary underline-offset-2 hover:text-fg dark:hover:text-text-primary hover:underline transition-colors"
        >
          {secondaryLabel}
        </a>
      )}
    </div>
  );
};
